import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowRight } from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY, FIT_COLORS, OPPORTUNITY_COLORS } from "./shared";

const BUCKET_COLORS = { ...OPPORTUNITY_COLORS, ...FIT_COLORS };

export default function ActionQueueTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getActionQueue({ limit: 20 });
      setItems(r.items || []);
      setTotalCandidates(r.total_candidates || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-400">
          Top {items.length} of {totalCandidates} opportunities, ranked by fit problem then impressions.
        </p>
        <button onClick={load} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">
          Loading...
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">
          No classified queries yet. Run Query Gap Analysis from the Queries tab first.
        </div>
      )}

      <div className="space-y-2">
        {items.map(item => {
          const bc = BUCKET_COLORS[item.bucket] || { bg: "#F4F4F5", text: "#52525B" };
          return (
            <div
              key={item.query_id}
              className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4 flex items-start gap-4"
            >
              <span className="text-lg font-bold text-zinc-300 tabular-nums w-8 shrink-0">
                {String(item.rank).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold uppercase rounded-full"
                    style={{ background: bc.bg, color: bc.text }}
                  >
                    {item.bucket.replace(/_/g, " ")}
                  </span>
                  <button
                    onClick={() => navigate(`/dashboard/query-gaps/${item.query_id}`)}
                    className="text-sm font-semibold text-zinc-900 hover:text-green-700 transition-colors truncate"
                  >
                    {item.query}
                  </button>
                </div>
                <p className="text-xs text-zinc-600 mb-1">{item.problem}</p>
                <p className="text-xs font-medium text-zinc-800 mb-1.5">→ {item.recommended_action}</p>
                <div className="flex items-center gap-3 text-[10px] text-zinc-400">
                  <span>{item.evidence}</span>
                  {item.article_title && <span className="truncate">Page: {item.article_title}</span>}
                </div>
              </div>
              <button
                onClick={() => navigate(`/dashboard/query-gaps/${item.query_id}`)}
                className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold rounded-lg shrink-0 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: PRIMARY, color: "#fff" }}
              >
                Open <ArrowRight size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
